import React from "react"
import ReportBadgeWithTooltip from "../../../../components/molecules/ReportBadgeWithTooltip.molecule"
import BeachAccessIcon from "@mui/icons-material/BeachAccess"
import BeachAccessIconOutlined from "@mui/icons-material/BeachAccessOutlined"
import CommentOutlined from "@mui/icons-material/CommentOutlined"
import { getVacationRangeLabel } from "../../../../utils/vacation.utils"
import { CommentFillIcon } from "../../../../design/assests/images/icons/comment-fill"
import { TriangleFillIcon } from "../../../../design/assests/images/icons/triangle-fill"
import useThemeContext from "../../../../hooks/contextConsumers/useThemeContext.hook"

export const CommentBadge = ({ comment, position }) => {
  const { theme } = useThemeContext()
  return (
    <ReportBadgeWithTooltip
      type="comment"
      iconClassName={theme === "light" ? "pi pi-comment text-white" : undefined}
      iconSvg={theme === "dark" ? <CommentFillIcon /> : undefined}
      position={position}
      tooltipValue={
        <>
          <span className="font-bold">Comentario: </span>
          <span>{comment}</span>
        </>
      }
    />
  )
}

export const EditedAfterFinishedBadge = ({ position }) => {
  return (
    <ReportBadgeWithTooltip
      type="edited"
      iconClassName="pi pi-history"
      tooltipValue="El día fue editado después de haber sido finalizado"
      position={position}
    />
  )
}

export const EmptyVacationDayBadge = ({
  vacationsDateRange,
  vacationsMotive,
  position,
}) => {
  const { theme } = useThemeContext()

  return (
    <ReportBadgeWithTooltip
      type="empty-vacation"
      iconSvg={
        theme === "dark" ?
          <BeachAccessIcon
            style={{
              color:
                "light-dark(var(--text-color__dark), var(--bucket-days-header__dark))",
            }}
          />
        : <BeachAccessIconOutlined />
      }
      position={position}
      tooltipValue={
        <div>
          <div>
            <span>Vacaciones: </span>
            {getVacationRangeLabel(
              null,
              vacationsDateRange[0],
              vacationsDateRange[1],
              {
                includeNumberOfDays: false,
                includeDateRange: true,
                dateFormat: "dd/MM/yyyy",
              }
            )}
          </div>
          <span>Motivo: {vacationsMotive}</span>
        </div>
      }
    />
  )
}

export const NonEmptyVacationDayBadge = ({ vacationsMotive, position }) => {
  const { theme } = useThemeContext()

  return (
    <ReportBadgeWithTooltip
      type="non-empty-vacation"
      iconSvg={
        theme === "dark" ? <BeachAccessIcon /> : <BeachAccessIconOutlined />
      }
      position={position}
      tooltipValue={
        <div>
          <div className="font-bold">Vacaciones con registro de tareas </div>
          {vacationsMotive && <span>Motivo: {vacationsMotive}</span>}
        </div>
      }
    />
  )
}

export const TimeDifferenceBadge = ({
  value,
  percentage,
  tooltip,
  minTreshold = 0.5,
  maxTreshold = 1,
  referenceValue = 40,
  position,
  ...props
}) => {
  const isDanger =
    value < referenceValue - minTreshold || value > referenceValue + maxTreshold

  const { theme } = useThemeContext()
  return (
    isDanger && (
      <ReportBadgeWithTooltip
        type="danger"
        iconClassName={
          theme === "light" ?
            "pi pi-exclamation-triangle text-white"
          : undefined
        }
        iconSvg={theme === "dark" ? <TriangleFillIcon /> : undefined}
        legend={percentage}
        legendProps={{
          style: {
            fontSize: "12px",
            fontWeight: "bold",
          },
        }}
        position={position}
        tooltipValue={tooltip}
        className={"ml-1 " + props.className}
        {...props}
      />
    )
  )
}

export const UnexpectedTimeBadge = ({
  value,
  expectedValue,
  tooltips = {},
  minThreshold = 0.5,
  maxThreshold = 1,
  position,
  ...props
}) => {
  const isDanger =
    value < expectedValue - minThreshold || value > expectedValue + maxThreshold
  const isLess = value < expectedValue
  const legendProps = {
    style: {
      fontSize: "12px",
      fontWeight: "bold",
    },
  }
  if (!isDanger) return null
  return (
    <ReportBadgeWithTooltip
      type="danger"
      iconClassName={`pi pi-${isLess ? "arrow-down" : "arrow-up"} text-white`}
      legendProps={legendProps}
      position={position}
      tooltipValue={tooltips[isLess ? "isLess" : "isMore"]}
      {...props}
    />
  )
}

const CellReportBadges = ({ rowData, date, position }) => {
  const currentDay = rowData?.week?.[date]
  if (!currentDay) return null
  const isDayEditedAfterFinished = Boolean(currentDay?.isDayEditedAfterFinished)
  const timeDifference = Boolean(currentDay?.sisuTime > currentDay?.billedTime)
  const isEmptyVacationDay = Boolean(
    currentDay?.isVacationDay && currentDay?.totalSisuTime === 0
  )
  const isNonEmptyVacationDay = Boolean(
    currentDay?.isVacationDay && currentDay?.totalSisuTime > 0
  )
  const isDayFinishedWithComment = Boolean(currentDay?.workDayComment)

  return (
    <span className="flex gap-1">
      {isEmptyVacationDay && (
        <EmptyVacationDayBadge
          vacationsDateRange={[
            currentDay?.vacationsStartDate,
            currentDay?.vacationsEndDate,
          ]}
          vacationsMotive={currentDay?.vacationsMotive}
          position={position}
        />
      )}
      {isNonEmptyVacationDay && (
        <NonEmptyVacationDayBadge
          vacationsMotive={currentDay?.workDayComment}
          position={position}
        />
      )}
      {isDayEditedAfterFinished && (
        <EditedAfterFinishedBadge position={position} />
      )}
      {isDayFinishedWithComment && !isNonEmptyVacationDay && (
        <CommentBadge
          comment={currentDay?.workDayComment}
          position={position}
        />
      )}
      {timeDifference && (
        <ReportBadgeWithTooltip
          type="danger"
          iconClassName="pi pi-exclamation-triangle text-white"
          lagend={timeDifference}
          position={position}
        />
      )}
    </span>
  )
}

export default CellReportBadges
